import { Injectable } from '@angular/core';
import {from, map, Observable, throwError, catchError} from "rxjs";
import {Organization} from "../models/organization";
import {
  collection,
  doc,
  DocumentData,
  DocumentReference,
  DocumentSnapshot,
  Firestore,
  getDoc,
  getDocs,
  limit,
  query,
  Query,
  setDoc
} from '@angular/fire/firestore';
import {AuthenticationService} from "./authentication.service";


@Injectable({
  providedIn: 'root'
})
export class OrganizationService {
  constructor(
    private firestore: Firestore,
    private authService: AuthenticationService
  ) { }

  /**
   * Get specific organization based on given document reference
   *
   * @param ref
   */
  public getOrganizationByRef(ref: string | DocumentReference): Observable<Organization> {
    let docRef;

    if (typeof ref === 'string') {
      docRef = doc(this.firestore, `organizations/${ref}`);
    } else {
      docRef = ref;
    }

    return from(getDoc(docRef)).pipe(
      map((snapshot: DocumentSnapshot<DocumentData>) => {
        const data = snapshot.data();
        if (data) {
          return {
            id: snapshot.id,
            ...data
          } as Organization;
        } else {
          throw new Error(`Document with reference: ${ref} does not exist`);
        }
      }),
      catchError(error => throwError(error))
    );
  }

  /**
   * Updates organization details in the organization document on firebase.
   *
   * @param id
   * @param orgName
   * @param description
   */
  public async changeOrganizationDetails(id: string, orgName: string, description: string ) {
    await setDoc(doc(this.firestore, `organizations/${id}`), {
      name: orgName,
      description
    }, { merge: true });
  }

  /**
   * Updates organization address in the organization document on firebase.
   *
   * @param id
   * @param zipcode
   * @param houseNumber
   * @param street
   * @param city
   */
  public async changeOrganizationAddress(id: string, zipcode: string, houseNumber: string, street: string, city: string) {
    await setDoc(doc(this.firestore, `organizations/${id}`), {
      zipcode,
      houseNumber,
      street,
      city
    }, { merge: true });
  }

  /**
   * Updates organization contact details in the organization document on firebase.
   *
   * @param id
   * @param contactName
   * @param contactPhone
   */
  public async changeOrganizationContact(id: string, contactName: string, contactPhone: string) {
    await setDoc(doc(this.firestore, `organizations/${id}`), {
      contactName,
      contactPhone
    }, { merge: true });
  }

  /**
   * Attempts to re-authenticate the user and then update the users Firebase email address.
   *
   * @param id
   * @param userEmail
   * @param userPass
   * @param newEmail
   */
  public async updateEmail(id: string, userEmail: string, userPass: string, newEmail: string) {
    await this.authService.reauthenticateUser(userEmail, userPass).then(async () => {
      await setDoc(doc(this.firestore, `organizations/${id}`), {
        email: newEmail
      }, {merge: true});
    }).catch((error) => {
      throw error;
    });
  }

  /**
   * Retrieves organizations from Firestore collection and returns them.
   */
  public async getOrganizations(offset: number): Promise<Organization[]> {
    const usersQuery = query(
      collection(this.firestore, "organizations"), limit(50)
    ) as Query<DocumentData>;

    const querySnapshot = await getDocs(usersQuery);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }) as Organization);
  }
}
